import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../lib/api";

interface BillingState {
  tier: "free" | "pro";
  hasPaidAccess: boolean;
  lifetime: boolean;
  accessSource: "free" | "subscription" | "one_time";
  planId: string;
  checkoutAvailable: boolean;
  price: { amount: 25; currency: "USD"; cadence: "lifetime" };
  purchasedAt: string | null;
}

interface CheckoutResponse {
  alreadyOwned: boolean;
  checkoutUrl: string | null;
  billing: BillingState;
}

const proBenefits = [
  "See the full check, not only the first three problems",
  "Work on several things at the same time",
  "Try what-if changes without overwriting your real numbers",
  "Share the plan with one household",
  "Keep policy notes and renewal dates together",
  "Download a report your family can understand",
  "Use the local guides we add later"
];

export function BillingPage({
  session,
  checkoutReturned,
  onDashboard
}: {
  session: Session;
  checkoutReturned: boolean;
  onDashboard: () => void;
}) {
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [message, setMessage] = useState<string | null>(
    checkoutReturned ? "Confirming your payment securely…" : null
  );
  const mounted = useRef(true);

  const loadBilling = useCallback(async () => {
    const state = await apiRequest<BillingState>("/api/v1/billing", session);
    if (mounted.current) setBilling(state);
    return state;
  }, [session]);

  useEffect(() => {
    mounted.current = true;
    void apiRequest("/api/v1/billing/viewed", session, { method: "POST", body: "{}" }).catch(() => undefined);
    if (checkoutReturned) {
      void apiRequest("/api/v1/billing/checkout-returned", session, { method: "POST", body: "{}" }).catch(() => undefined);
    }
    void (async () => {
      try {
        const first = await loadBilling();
        if (checkoutReturned && !first.hasPaidAccess) {
          for (let attempt = 0; attempt < 7 && mounted.current; attempt += 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 1_500));
            const refreshed = await loadBilling();
            if (refreshed.hasPaidAccess) break;
          }
        }
      } catch (error) {
        if (mounted.current) {
          setMessage(error instanceof Error ? error.message : "Pro access could not be checked.");
        }
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, [checkoutReturned, loadBilling]);

  useEffect(() => {
    if (!billing?.hasPaidAccess || !checkoutReturned) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    url.searchParams.delete("upgrade");
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    setMessage("Payment confirmed. Pro is now on this account for life.");
  }, [billing?.hasPaidAccess, checkoutReturned]);

  async function startCheckout() {
    setBuying(true);
    setMessage(null);
    try {
      const result = await apiRequest<CheckoutResponse>("/api/v1/billing/checkout", session, {
        method: "POST",
        body: "{}"
      });
      if (result.alreadyOwned) {
        setBilling(result.billing);
        setMessage("MoneyMap Pro is already active on this account.");
        return;
      }
      if (!result.checkoutUrl) throw new Error("Checkout is temporarily unavailable.");
      window.location.assign(result.checkoutUrl);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Checkout could not be opened.");
      setBuying(false);
    }
  }

  if (loading && !billing) {
    return <main className="center-panel" id="main-content"><div className="spinner" /><p>Checking your MoneyMap access…</p></main>;
  }

  const isPro = billing?.hasPaidAccess ?? false;
  return (
    <main className="billing-page" id="main-content">
      <section className="billing-hero">
        <div className="page-width billing-hero-grid">
          <div>
            <p className="eyebrow">$25 once. No subscription.</p>
            <h1>{isPro ? "Pro is yours for life." : "The useful part is free. Pay only if you want to keep going."}</h1>
            <p className="hero-lede">Free shows your three biggest gaps and what to do first. Pro opens the full check, comparisons, shared access and records you’ll want to revisit later.</p>
            {message && <p className={`billing-message ${isPro ? "success" : ""}`} role="status">{message}</p>}
            <div className="billing-actions">
              {isPro ? (
                <button className="primary-button large" type="button" onClick={onDashboard}>Open my plan <span>→</span></button>
              ) : (
                <button className="primary-button large" disabled={buying || !billing?.checkoutAvailable} type="button" onClick={() => { void startCheckout(); }}>
                  {buying ? "Opening secure checkout…" : "Get lifetime Pro for $25"} <span>→</span>
                </button>
              )}
              {!isPro && <button className="secondary-button large" type="button" onClick={onDashboard}>Continue with Free</button>}
            </div>
            <div className="trust-row"><span>✓ One payment</span><span>✓ Tied to your account</span><span>✓ No automatic renewal</span></div>
          </div>
          <div className={`pro-pass ${isPro ? "unlocked" : ""}`} aria-label={isPro ? "MoneyMap Pro lifetime pass active" : "MoneyMap Pro lifetime pass"}>
            <span className="pro-pass-top">MONEYMAP · PRO</span>
            <strong>{isPro ? "UNLOCKED" : "$25"}</strong>
            <p>{isPro ? "Lifetime access" : "Pay once · use for life"}</p>
            <div className="pro-pass-orbit"><span>∞</span></div>
            <small>{isPro && billing?.purchasedAt ? `Active since ${new Date(billing.purchasedAt).toLocaleDateString()}` : "All countries · current and future guides"}</small>
          </div>
        </div>
      </section>

      <section className="page-width billing-value">
        <div className="billing-plan-card free-card"><p className="eyebrow">Enough to make a real start</p><h2>Free</h2><div className="billing-price">$0 <small>forever</small></div><ul><li>A complete first household check</li><li>Your three most important problems</li><li>Cash, health cover, life cover, debt and goals checked together</li><li>One action to work on</li><li>The calculation and assumptions behind every result</li><li>Saved answers you can change later</li></ul></div>
        <div className="billing-plan-card pro-card"><div className="current-tier">{isPro ? "You own this" : "For people who want the full plan"}</div><p className="eyebrow">Everything above, plus</p><h2>Pro</h2><div className="billing-price">$25 <small>once</small></div><ul>{proBenefits.map((benefit) => <li key={benefit}>{benefit}</li>)}</ul>{!isPro && <button className="primary-button full-width" disabled={buying || !billing?.checkoutAvailable} type="button" onClick={() => { void startCheckout(); }}>Get Pro for life</button>}</div>
      </section>

      <section className="page-width billing-proof"><div><span>1</span><strong>Use your MoneyMap email</strong><p>That is the account that will own Pro.</p></div><div><span>2</span><strong>Pay on Dodo’s checkout</strong><p>We don’t handle your card details.</p></div><div><span>3</span><strong>Come straight back</strong><p>Pro unlocks on the same account and does not expire.</p></div></section>
    </main>
  );
}
